import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { CalendarClock, MoreHorizontal, PanelLeft, Pin, Search, SlidersHorizontal, Smartphone, SquarePen, FolderKanban } from 'lucide-react';
import { ChatActionsMenu } from './ChatActionsMenu';
import { EditableChatTitle, type EditableChatTitleHandle } from './EditableChatTitle';
import { deleteAiChat, renameAiChat } from '../utils/chatApi';
import {
  applyPinnedAt,
  groupAiChats,
  type AiChatHistoryItem,
} from '../utils/groupAiChats';

/** Einzeilige Zeilen: `py-1.5` plus `leading-5`. Steuert nur die Seitengröße beim Nachladen. */
const ITEM_HEIGHT_PX = 32;

/** Eine Schriftgröße für die ganze Spalte — Hierarchie kommt über Farbe, nicht über Größe.
 *  Die Chat-Titel erben sie von der Zeile (`EditableChatTitle` setzt selbst keine Größe). */
const SIDEBAR_TEXT_CLASS = 'text-sm';

/** „Neuer Chat" und „Chats durchsuchen" sind dieselbe Zeilen-Optik. */
const SIDEBAR_ACTION_CLASS =
  `flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-app-text-muted transition-colors hover:bg-app-hover ${SIDEBAR_TEXT_CLASS}`;
const MIN_PAGE_SIZE = 8;
/** Platz für „ältere Chats laden“ und die Datumsüberschriften, damit die Zeile nicht erst nach dem Rendern nach unten rutscht. */
const LIST_CHROME_RESERVE_PX = 36 + 28 * 2;

function calcPageSize(containerHeight: number): number {
  const usable = Math.max(ITEM_HEIGHT_PX, containerHeight - LIST_CHROME_RESERVE_PX);
  return Math.max(MIN_PAGE_SIZE, Math.floor(usable / ITEM_HEIGHT_PX));
}

export type { AiChatHistoryItem };

export type ChatPinPatch = {
  chatId: string;
  pinnedAt: string | null;
  token: number;
};

type Props = {
  currentChatId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onOpenSearch: () => void;
  isSearchingActive?: boolean;
  refreshKey?: number;
  onChatRenamed?: (chatId: string, title: string) => void;
  onChatsLoaded?: (chats: AiChatHistoryItem[]) => void;
  onTogglePin?: (chatId: string, pinned: boolean, previousPinnedAt: string | null) => void;
  pinPatch?: ChatPinPatch | null;
  activeView?: string | null;
  onOpenView?: (view: 'projects' | 'customize' | 'scheduled' | null) => void;
  projectId?: string | null;
  onClearProject?: () => void;
  onCollapse?: () => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
  collapseLabel?: string;
  open?: boolean;
};

function historyItemsFromApi(rows: unknown): AiChatHistoryItem[] {
  if (!Array.isArray(rows)) return [];
  const items: AiChatHistoryItem[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const rec = row as Record<string, unknown>;
    if (typeof rec.id !== 'string' || typeof rec.title !== 'string') continue;
    if (typeof rec.created_at !== 'string' || typeof rec.updated_at !== 'string') continue;
    items.push({
      id: rec.id,
      title: rec.title,
      created_at: rec.created_at,
      updated_at: rec.updated_at,
      pinned_at: typeof rec.pinned_at === 'string' ? rec.pinned_at : null,
      access: rec.access === 'read' ? 'read' : 'owner',
      project_id: typeof rec.project_id === 'string' ? rec.project_id : null,
      folder_id: typeof rec.folder_id === 'string' ? rec.folder_id : null,
    });
  }
  return items;
}

export function ChatHistorySidebar({
  currentChatId,
  onSelect,
  onNew,
  onOpenSearch,
  isSearchingActive = false,
  refreshKey,
  onChatRenamed,
  onChatsLoaded,
  onTogglePin,
  pinPatch,
  activeView = null,
  onOpenView,
  projectId = null,
  onClearProject,
  onCollapse,
  onHoverStart,
  onHoverEnd,
  collapseLabel = 'Seitenleiste ausblenden',
  open = true,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);
  const loadRequestRef = useRef(0);
  const [pageSize, setPageSize] = useState(MIN_PAGE_SIZE);
  const [pageSizeReady, setPageSizeReady] = useState(false);
  const [chats, setChats] = useState<AiChatHistoryItem[]>([]);
  const [sharedChats, setSharedChats] = useState<AiChatHistoryItem[]>([]);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [projectsReady, setProjectsReady] = useState(false);
  const [placingChatId, setPlacingChatId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [menuChatId, setMenuChatId] = useState<string | null>(null);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  /** Der Umbenennen-Eintrag im Zeilenmenü schaltet den Titel der jeweiligen Zeile in den Edit-Modus. */
  const titleHandlesRef = useRef(new Map<string, EditableChatTitleHandle>());

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let locked = false;
    const apply = () => {
      if (locked) return;
      const height = el.clientHeight;
      if (height <= 0) return;
      locked = true;
      setPageSize(calcPageSize(height));
      setPageSizeReady(true);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const loadChats = useCallback(async (limit: number) => {
    const requestId = ++loadRequestRef.current;
    try {
      const scope = projectId ? `&projectId=${encodeURIComponent(projectId)}` : '';
      const res = await fetch(`/api/ai/chats?limit=${limit}&offset=0${scope}`, { credentials: 'include' });
      if (!res.ok || requestId !== loadRequestRef.current) return;
      const data: unknown = await res.json();
      if (requestId !== loadRequestRef.current) return;
      const payload = data && typeof data === 'object' ? data as Record<string, unknown> : {};
      const list = historyItemsFromApi(payload.chats);
      setChats(list);
      setSharedChats(historyItemsFromApi(payload.sharedChats));
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
      onChatsLoaded?.(list);
    } catch {
      // silently ignore
    }
  }, [onChatsLoaded, projectId]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const scope = projectId ? `&projectId=${encodeURIComponent(projectId)}` : '';
      const res = await fetch(`/api/ai/chats?limit=${pageSize}&offset=${chats.length}${scope}`, { credentials: 'include' });
      if (!res.ok) return;
      const data: unknown = await res.json();
      const payload = data && typeof data === 'object' ? data as Record<string, unknown> : {};
      const more = historyItemsFromApi(payload.chats);
      setChats((prev) => {
        const next = [...prev, ...more];
        onChatsLoaded?.(next);
        return next;
      });
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
    } catch {
      // silently ignore
    } finally {
      setLoadingMore(false);
    }
  }, [chats.length, pageSize, onChatsLoaded, projectId]);

  useEffect(() => {
    if (!pageSizeReady) return;
    void loadChats(pageSize);
  }, [loadChats, pageSize, pageSizeReady, refreshKey]);

  useLayoutEffect(() => {
    if (!pinPatch) return;
    setChats((prev) => applyPinnedAt(prev, pinPatch.chatId, pinPatch.pinnedAt));
  }, [pinPatch]);

  const handleDelete = async (chatId: string) => {
    setDeletingId(chatId);
    try {
      await deleteAiChat(chatId);
      setChats((prev) => prev.filter((c) => c.id !== chatId));
      if (currentChatId === chatId) onNew();
    } catch {
      // silently ignore (wie bisher)
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    if (!projectId) {
      setProjectName(null);
      return;
    }
    let cancelled = false;
    void fetch('/api/ai/chat-projects', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() as Promise<{ projects?: Array<{ id: string; name: string }> }> : { projects: [] }))
      .then((body) => {
        if (cancelled) return;
        setProjectName(body.projects?.find((project) => project.id === projectId)?.name ?? 'Projekt');
      })
      .catch(() => {
        if (!cancelled) setProjectName('Projekt');
      });
    return () => { cancelled = true; };
  }, [projectId]);

  const groups = groupAiChats(chats);

  const placeChat = async (chatId: string, nextProjectId: string | null) => {
    const res = await fetch(`/api/ai/chats/${encodeURIComponent(chatId)}/placement`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectId: nextProjectId }),
    });
    setPlacingChatId(null);
    if (res.ok) void loadChats(pageSize);
  };

  const openProjectPicker = async (chatId: string) => {
    setPlacingChatId(chatId);
    setProjectsReady(false);
    try {
      const res = await fetch('/api/ai/chat-projects', { credentials: 'include' });
      if (!res.ok) return;
      const body = await res.json() as { projects?: Array<{ id: string; name: string }> };
      setProjects(body.projects ?? []);
    } catch {
      setProjects([]);
    } finally {
      setProjectsReady(true);
    }
  };

  const renderChatRow = (chat: AiChatHistoryItem) => {
    const isActive = currentChatId === chat.id;
    const isMenuOpen = menuChatId === chat.id;
    const isPinned = Boolean(chat.pinned_at);
    return (
      <div
        key={chat.id}
        className={`group relative flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${SIDEBAR_TEXT_CLASS} ${
          deletingId === chat.id ? 'pointer-events-none opacity-50' : ''
        } ${
          isActive
            ? 'bg-blue-50 text-blue-700'
            : `text-app-text-muted hover:bg-app-hover ${isMenuOpen ? 'bg-app-surface-muted' : ''}`
        }`}
      >
        {isPinned ? (
          <Pin
            className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-blue-500' : 'text-slate-400'}`}
            aria-hidden
          />
        ) : chat.channel === 'field' ? (
          <Smartphone
            className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-blue-500' : 'text-slate-400'}`}
            aria-label="Aus der App"
          />
        ) : (
          <span
            aria-hidden
            className={`h-1.5 w-1.5 shrink-0 rounded-full border ${
              isActive ? 'border-blue-400 bg-blue-400' : 'border-app-border-strong'
            }`}
          />
        )}
        <EditableChatTitle
          ref={(handle) => {
            if (handle) titleHandlesRef.current.set(chat.id, handle);
            else titleHandlesRef.current.delete(chat.id);
          }}
          title={chat.title}
          compact
          className="min-w-0 flex-1"
          onTitleClick={() => onSelect(chat.id)}
          onEditingChange={(editing) =>
            setEditingChatId((prev) => (editing ? chat.id : prev === chat.id ? null : prev))
          }
          onSave={async (nextTitle) => {
            const savedTitle = await renameAiChat(chat.id, nextTitle);
            setChats((prev) =>
              prev.map((item) =>
                item.id === chat.id ? { ...item, title: savedTitle } : item,
              ),
            );
            onChatRenamed?.(chat.id, savedTitle);
            return savedTitle;
          }}
        />
        {editingChatId === chat.id ? null : (
          <ChatActionsMenu
            align="right"
            isPinned={isPinned}
            onOpenChange={(open) => setMenuChatId(open ? chat.id : null)}
            onRename={() => titleHandlesRef.current.get(chat.id)?.startEditing()}
            onDelete={() => void handleDelete(chat.id)}
            extraItems={chat.access === 'read' ? undefined : [
              { label: 'In Projekt…', onClick: () => void openProjectPicker(chat.id) },
            ]}
            onTogglePin={
              onTogglePin
                ? () => onTogglePin(chat.id, !isPinned, chat.pinned_at)
                : undefined
            }
            triggerClassName={`absolute right-1 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md transition-opacity focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-pipedrive-blue ${
              isActive
                ? 'bg-blue-50 text-blue-500 hover:bg-blue-100'
                : 'bg-app-surface-muted text-slate-400 hover:bg-slate-200/70 hover:text-app-text-muted'
            } ${isMenuOpen ? 'opacity-100' : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100'}`}
            renderTrigger={() => <MoreHorizontal className="h-3.5 w-3.5" />}
          />
        )}
      </div>
    );
  };

  return (
    // Breite ändern? Dann `VIEWPORT_CENTER` in `AiChatPanel` mitziehen — dort steckt die halbe Breite.
    <aside
      className={`relative z-20 hidden h-full min-h-0 min-w-0 shrink-0 flex-col overflow-hidden border-r bg-app-surface transition-[width] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none md:flex ${
        open ? 'border-app-border' : 'pointer-events-none border-transparent'
      }`}
      style={{ width: open ? 260 : 0 }}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      aria-hidden={!open}
    >
      <div className="flex h-full w-[260px] min-h-0 flex-col">
      {/* Wie in Gemini: zwei volle Zeilen ohne Rahmen/Füllung, Fläche erst beim Hover.
          `pt-1.5` setzt die erste Zeile auf die Höhe des Chat-Titels in `ChatHeader`. */}
      <div className="shrink-0 space-y-0.5 px-2 pt-1.5 pb-1">
        <div className="flex items-center gap-0.5">
          <button type="button" onClick={onNew} className={`${SIDEBAR_ACTION_CLASS} min-w-0 flex-1`}>
            <SquarePen className="h-4 w-4 shrink-0 text-app-text-subtle" />
            Neuer Chat
          </button>
          <button
            type="button"
            onClick={onCollapse}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
            aria-label={collapseLabel}
            title={collapseLabel}
          >
            <PanelLeft className="h-4 w-4" />
          </button>
        </div>
        <button
          type="button"
          onClick={onOpenSearch}
          className={`${SIDEBAR_ACTION_CLASS} ${isSearchingActive ? 'bg-blue-50 font-medium text-blue-700 hover:bg-blue-100/70' : ''}`}
        >
          <Search className={`h-4 w-4 shrink-0 ${isSearchingActive ? 'text-blue-600' : 'text-app-text-subtle'}`} />
          Chats durchsuchen
        </button>
        {([
          ['projects', 'Projekte', FolderKanban],
          ['customize', 'Anpassungen', SlidersHorizontal],
          ['scheduled', 'Geplant', CalendarClock],
        ] as const).map(([view, label, Icon]) => (
          <button
            key={view}
            type="button"
            onClick={() => onOpenView?.(activeView === view ? null : view)}
            className={`${SIDEBAR_ACTION_CLASS} ${activeView === view ? 'bg-app-hover font-medium text-app-text' : ''}`}
          >
            <Icon className="h-4 w-4 shrink-0 text-app-text-subtle" />
            {label}
          </button>
        ))}
        {projectId ? (
          <div className={`${SIDEBAR_ACTION_CLASS} hover:bg-transparent`}>
            <FolderKanban className="h-4 w-4 shrink-0 text-app-text-subtle" />
            <span className="min-w-0 flex-1 truncate text-app-text">{projectName ?? 'Projekt'}</span>
            <button type="button" className="shrink-0 text-xs text-app-text-subtle hover:text-app-text" onClick={onClearProject}>
              Alle Chats
            </button>
          </div>
        ) : null}
      </div>

      <div ref={listRef} className="app-scrollbar flex-1 overflow-y-auto overscroll-contain px-2 pb-4">
        {chats.length === 0 && sharedChats.length === 0 && (
          <p className="text-xs text-slate-400 text-center mt-6 px-2">
            {projectId ? 'Noch keine Chats in diesem Projekt.' : 'Noch keine Chats gespeichert.'}
          </p>
        )}
        {!projectId && sharedChats.length > 0 ? (
          <div className="mb-1">
            <p className={`px-2 pt-3 pb-1 font-normal text-slate-400 ${SIDEBAR_TEXT_CLASS}`}>Geteilt</p>
            {sharedChats.map((chat) => {
              const isActive = currentChatId === chat.id;
              return (
                <button
                  key={chat.id}
                  type="button"
                  onClick={() => onSelect(chat.id)}
                  className={`flex w-full items-center rounded-lg px-2 py-1.5 text-left ${SIDEBAR_TEXT_CLASS} ${isActive ? 'bg-blue-50 text-blue-700' : 'text-app-text hover:bg-app-hover'}`}
                >
                  <span className="min-w-0 flex-1 truncate">{chat.title}</span>
                </button>
              );
            })}
          </div>
        ) : null}
        {groups.map(({ key, label, items }) => (
          <div key={key} className="mb-1">
            <p className={`px-2 pt-3 pb-1 font-normal text-slate-400 ${SIDEBAR_TEXT_CLASS}`}>{label}</p>
            {items.map((chat) => renderChatRow(chat))}
          </div>
        ))}
        {placingChatId ? (
          <div className="mt-2 rounded-md border border-app-border bg-app-surface p-2">
            <p className="mb-1 text-xs text-app-text-muted">Projekt wählen</p>
            <button type="button" className={`${SIDEBAR_ACTION_CLASS} px-2`} onClick={() => void placeChat(placingChatId, null)}>Kein Projekt</button>
            {projects.map((project) => (
              <button key={project.id} type="button" className={`${SIDEBAR_ACTION_CLASS} px-2`} onClick={() => void placeChat(placingChatId, project.id)}>
                <span className="truncate">{project.name}</span>
              </button>
            ))}
            {projectsReady && projects.length === 0 ? <p className="px-2 py-1 text-xs text-app-text-muted">Noch keine Projekte.</p> : null}
          </div>
        ) : null}
        {chats.length < total && (
          <button
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className={`w-full mt-1 px-2 py-1.5 rounded-lg text-slate-400 hover:text-app-text-muted hover:bg-app-hover transition-colors disabled:opacity-50 ${SIDEBAR_TEXT_CLASS}`}
          >
            {loadingMore ? 'Lädt…' : `${Math.min(20, total - chats.length)} ältere Chats laden`}
          </button>
        )}
      </div>
      </div>
    </aside>
  );
}
